/**
 * Workshop 模块 API 工厂函数
 * 
 * @module api/workshop/factory
 * @description 车间管理模块的 API 工厂函数
 * 
 * @backend backend/app/Modules/Organization/Controllers/WorkshopController.php
 * @routes /api/v1/workshops
 * 
 * @author YSFL Team
 * @created 2025-10-24
 * @last-updated 2025-10-24
 */

// ===== 导入类型定义 =====
import type {
  Workshop,
  WorkshopQueryParams,
  WorkshopAllQueryParams,
  CreateWorkshopForm,
  UpdateWorkshopForm,
  BatchImportWorkshopForm,
  UpdateOrderForm,
  WorkshopFindByCodeParams,
  WorkshopStatistics,
  ProductionLine,
} from '../../types/organization';

import type { PaginationApiResponse, BatchDeleteResult, BatchImportResult } from '../../types/api';
import type { IRequestClient } from '../core/client';

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes';

/**
 * 创建 Workshop API 实例
 * 
 * @param client HTTP 请求客户端
 * @returns Workshop API 实例
 */
export function createWorkshopApi(client: IRequestClient) {
  return {
    /**
     * 获取车间列表（分页）
     */
    getList(params?: WorkshopQueryParams) {
      return client.get<PaginationApiResponse<Workshop>>(API_ROUTES.workshop.list, { params });
    },

    /**
     * 获取所有车间（不分页）
     */
    getAll(params?: WorkshopAllQueryParams) {
      return client.get<Workshop[]>(API_ROUTES.workshop.all, { params });
    },

    /**
     * 获取车间详情
     */
    getDetail(id: number) {
      return client.get<Workshop>(API_ROUTES.workshop.detail(id));
    },

    /**
     * 创建车间
     */
    create(data: CreateWorkshopForm) {
      return client.post<Workshop>(API_ROUTES.workshop.create, data);
    },

    /**
     * 更新车间
     */
    update(id: number, data: UpdateWorkshopForm) {
      return client.put<Workshop>(API_ROUTES.workshop.workshops(id), data);
    },

    /**
     * 删除车间
     */
    delete(id: number) {
      return client.delete<void>(API_ROUTES.workshop.delete(id));
    },

    /**
     * 批量删除车间
     */
    batchDelete(ids: number[]) {
      return client.post<BatchDeleteResult>(API_ROUTES.workshop.batchDelete, { ids });
    },

    /**
     * 批量导入车间
     */
    import(data: BatchImportWorkshopForm) {
      return client.post<BatchImportResult>(API_ROUTES.workshop.import, data);
    },

    /**
     * 更新车间排序
     */
    updateOrder(data: UpdateOrderForm) {
      return client.put<void>(API_ROUTES.workshop.order, data);
    },

    /**
     * 根据车间编码查询
     */
    findByCode(params: WorkshopFindByCodeParams) {
      return client.get<Workshop>(API_ROUTES.workshop.findByCode, { params });
    },

    /**
     * 获取车间统计信息
     */
    getStatistics(id: number) {
      return client.get<WorkshopStatistics>(API_ROUTES.workshop.statistics(id));
    },

    /**
     * 获取车间的生产线列表
     */
    getProductionLines(id: number) {
      return client.get<ProductionLine[]>(API_ROUTES.workshop.productionLines(id));
    },
  };
}

/**
 * Workshop API 类型
 */
export type WorkshopApi = ReturnType<typeof createWorkshopApi>;

/**
 * 默认导出
 */
export default createWorkshopApi;

